import type { LifecycleMilestone, LifecycleNext } from '@cou/admin-ui';
import type { ContestState } from './state-pill';
import { formatRailDate } from './format-date';

export interface ScheduleRailContest {
  readonly state: ContestState;
  readonly createdAt: string;
  readonly finalisedAt: string | null;
  readonly opensAt: string;
  readonly closesAt: string | null;
}

export interface ScheduleRail {
  readonly milestones: readonly LifecycleMilestone[];
  readonly next: LifecycleNext | null;
}

/**
 * The Overview tab's Created/Finalised/Opens/Closes/Settled rail (admin-ui.md). AdminContest
 * carries no distinct "openedAt"/"closedAt"/"settledAt" actual-transition timestamps — only the
 * scheduled opensAt/closesAt and finalisedAt — so milestone dates reuse those scheduled times;
 * "Settled" has no timestamp source at all yet and always renders without a date.
 */
export function buildScheduleRail(contest: ScheduleRailContest): ScheduleRail {
  const canceled = contest.state === 'Canceled';
  const finalised = contest.finalisedAt !== null;
  const opened =
    contest.state === 'Open' || contest.state === 'Closed' || contest.state === 'Settled';
  const closed = contest.state === 'Closed' || contest.state === 'Settled';
  const settled = contest.state === 'Settled';

  // Canceled has no "next" lifecycle step, and AdminContest carries no history of which state a
  // canceled contest had reached, so Opens/Closes/Settled render as unreached rather than guessing.
  const milestones: LifecycleMilestone[] = [
    { label: 'Created', date: formatRailDate(contest.createdAt), state: 'done' },
    {
      label: 'Finalised',
      date: finalised ? formatRailDate(contest.finalisedAt) : undefined,
      state: finalised ? 'done' : canceled ? 'future' : 'current',
    },
    {
      label: 'Opens',
      date: formatRailDate(contest.opensAt),
      state: opened ? 'done' : canceled ? 'future' : finalised ? 'current' : 'future',
    },
    {
      label: 'Closes',
      date: contest.closesAt ? formatRailDate(contest.closesAt) : undefined,
      state: closed ? 'done' : canceled ? 'future' : opened ? 'current' : 'future',
    },
    {
      label: 'Settled',
      state: settled ? 'done' : canceled ? 'future' : closed ? 'current' : 'future',
    },
  ];

  let next: LifecycleNext | null;
  if (canceled) {
    next = null;
  } else if (!finalised) {
    next = { headline: 'Awaiting finalise', note: 'Pending admin action' };
  } else if (!opened) {
    next = { headline: 'Opens', date: formatRailDate(contest.opensAt) };
  } else if (!closed) {
    next = {
      headline: 'Closes',
      date: contest.closesAt ? formatRailDate(contest.closesAt) : undefined,
    };
  } else if (!settled) {
    next = { headline: 'Awaiting settlement' };
  } else {
    next = null;
  }

  return { milestones, next };
}
